export class LoggingAnalyticsHandler {
  trackPageView(url) {
    console.log('Analytics page view:', url); // eslint-disable-line no-console
  }

  trackUser(user) {
    console.log('Analytics track user:', user?.id);
  }

  trackCheckout(event, data) {
    console.log('Analytics track checkout:', event, data);
  }
}

// Google Analytics 4 (GA4) using gtag.js script, which is included in util/includeScripts.js
export class GoogleAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    // GA4 property. Manually send page_view events
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    // Note 1: You should turn "Enhanced measurement" off.
    //         It attaches own listeners to elements and that breaks in-app navigation.
    // Note 2: If previousPath is null (just after page load), gtag script sends page_view event automatically.
    //         Only in-app navigation needs to be sent manually from SPA.
    // Note 3: Timeout is needed because gtag script picks up <title>,
    //         and location change event happens before initial rendering.
    if (previousPath && window.gtag) {
      window.setTimeout(() => {
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
        });
      }, 300);
    }
  }

  // Track user properties in a privacy-compliant way
  trackUser(user) {
    if (!window.gtag || !user?.id) {
      return;
    }

    // Set user ID for cross-device tracking using the internal ID
    gtag('config', window.GA_MEASUREMENT_ID, {
      user_id: user.id
    });

    // Get profile completion percentage
    const getProfileCompletionStatus = (profile) => {
      if (!profile) return 0;
      
      const steps = {
        has_avatar: !!profile.publicData?.avatar,
        has_bio: !!profile.bio,
        has_location: !!profile.publicData?.location,
        email_verified: !!user.attributes?.emailVerified
      };
      
      const completed = Object.values(steps).filter(Boolean).length;
      const total = Object.keys(steps).length;
      return Math.round((completed / total) * 100);
    };

    // Calculate account age in days
    const getAccountAge = (createdAt) => {
      if (!createdAt) return 0;
      const created = new Date(createdAt);
      const now = new Date();
      const diffTime = Math.abs(now - created);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // Set user properties that don't contain PII
    gtag('set', 'user_properties', {
      user_type: user.attributes?.profile?.publicData?.userType || 'customer',
      account_age_days: getAccountAge(user.attributes?.createdAt),
      profile_completion: getProfileCompletionStatus(user.attributes?.profile),
      is_email_verified: !!user.attributes?.emailVerified,
      has_listings: user.attributes?.profile?.publicData?.hasListings || false,
      has_stripe_account: !!user.attributes?.stripeConnected,
      preferred_language: user.attributes?.profile?.preferredLanguage,
      country: user.attributes?.profile?.publicData?.location?.country
    });

    // Track user login event
    gtag('event', 'user_login', {
      method: 'email', // or could be 'facebook', 'google', etc.
      user_type: user.attributes?.profile?.publicData?.userType || 'customer'
    });
  }

  // Track checkout events
  trackCheckout(event, data) {
    if (!window.gtag) {
      return;
    }

    const safeData = {
      ...data,
      timestamp: new Date().toISOString(),
    };

    // Remove any potential PII
    delete safeData.email;
    delete safeData.name;
    delete safeData.phone;
    delete safeData.address;
    delete safeData.inquiryMessage; // Don't track the actual message content

    switch (event) {
      case 'begin_checkout':
        gtag('event', 'begin_checkout', {
          listing_id: safeData.listingId,
          listing_type: safeData.listingType,
          process_name: safeData.processName,
          currency: safeData.currency,
          value: safeData.price
        });
        break;

      case 'inquiry_submitted':
        gtag('event', 'generate_lead', {
          listing_id: safeData.listingId,
          listing_type: safeData.listingType,
          process_name: safeData.processName,
          currency: safeData.currency,
          value: safeData.price
        });
        break;

      case 'inquiry_error':
        gtag('event', 'inquiry_error', {
          listing_id: safeData.listingId,
          error_type: safeData.errorType,
          error_message: safeData.errorMessage
        });
        break;
    }
  }

  // Track user engagement events
  trackUserEngagement(eventName, eventData = {}) {
    if (!window.gtag) {
      return;
    }

    // Ensure no PII is included in the event data
    const safeEventData = {
      ...eventData,
      timestamp: new Date().toISOString(),
    };

    // Remove any potential PII fields
    delete safeEventData.email;
    delete safeEventData.name;
    delete safeEventData.phone;
    delete safeEventData.address;

    gtag('event', eventName, safeEventData);
  }
}

// Meta/Facebook Pixel Handler
export class FacebookPixelHandler {
  trackPageView(canonicalPath, previousPath) {
    // Only track page views for in-app navigation, not initial page load
    // Initial page view is tracked automatically by the pixel
    if (previousPath && window.fbq) {
      // Use a timeout similar to Google Analytics to ensure page has rendered
      window.setTimeout(() => {
        window.fbq('track', 'PageView');
      }, 300);
    }
  }

  trackUser(user) {
    if (!window.fbq || !user?.id) {
      return;
    }

    // Track user properties (ensure no PII is sent)
    const userData = {
      // Use internal user ID for tracking
      external_id: user.id,
      // User type for audience segmentation
      user_type: user.attributes?.profile?.publicData?.userType || 'customer',
      // Account verification status
      email_verified: !!user.attributes?.emailVerified,
      // Whether user has listings
      has_listings: user.attributes?.profile?.publicData?.hasListings || false,
      // Whether user has stripe account connected
      has_stripe_account: !!user.attributes?.stripeConnected,
    };

    // Set user data for advanced matching
    window.fbq('init', window.REACT_APP_META_PIXEL_ID, userData);
  }

  trackCheckout(event, data) {
    if (!window.fbq) {
      return;
    }

    const safeData = {
      ...data,
      timestamp: new Date().toISOString(),
    };

    // Remove any potential PII
    delete safeData.email;
    delete safeData.name;
    delete safeData.phone;
    delete safeData.address;
    delete safeData.inquiryMessage;

    switch (event) {
      case 'begin_checkout':
        window.fbq('track', 'InitiateCheckout', {
          content_type: 'listing',
          content_ids: [safeData.listingId],
          value: safeData.price,
          currency: safeData.currency
        });
        break;

      case 'inquiry_submitted':
        window.fbq('track', 'Lead', {
          content_type: 'listing',
          content_ids: [safeData.listingId],
          value: safeData.price,
          currency: safeData.currency
        });
        break;

      case 'purchase_completed':
        window.fbq('track', 'Purchase', {
          content_type: 'listing',
          content_ids: [safeData.listingId],
          value: safeData.price,
          currency: safeData.currency
        });
        break;

      case 'listing_viewed':
        window.fbq('track', 'ViewContent', {
          content_type: 'listing',
          content_ids: [safeData.listingId],
          value: safeData.price,
          currency: safeData.currency
        });
        break;

      case 'search_performed':
        window.fbq('track', 'Search', {
          search_string: safeData.searchQuery,
          content_category: safeData.category
        });
        break;
    }
  }

  // Track custom events
  trackUserEngagement(eventName, eventData = {}) {
    if (!window.fbq) {
      return;
    }

    // Ensure no PII is included in the event data
    const safeEventData = {
      ...eventData,
      timestamp: new Date().toISOString(),
    };

    // Remove any potential PII fields
    delete safeEventData.email;
    delete safeEventData.name;
    delete safeEventData.phone;
    delete safeEventData.address;

    // Map custom events to Facebook standard events or use custom events
    const eventMapping = {
      'listing_favorited': 'AddToWishlist',
      'user_registered': 'CompleteRegistration',
      'profile_completed': 'CompleteRegistration',
      'contact_clicked': 'Contact',
    };

    const fbEventName = eventMapping[eventName] || eventName;
    
    if (eventMapping[eventName]) {
      // Use standard Facebook events
      window.fbq('track', fbEventName, safeEventData);
    } else {
      // Use custom events for non-standard tracking
      window.fbq('trackCustom', fbEventName, safeEventData);
    }
  }
}
